import { Fragment, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import type { Invoice } from '@fleet/shared';
import { euro, fmtDate, monthLabel } from '@fleet/shared';
import { Badge, Button } from '../../design-system';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { invoiceDocumentUrl } from './invoices.api';
import { AutoLink } from '../../components/ui/EntiteitLink';
import { ENTITEIT_LINK } from '../../components/ui/entiteitLinkStijl';
import { ZoekBalk } from '../../components/ui/ZoekBalk';

const COLS = '24px 110px 1fr 100px 70px 110px 100px';

function invoiceTotal(inv: Invoice): number {
  return inv.regels.reduce((s, r) => s + r.bedragExcl, 0);
}

/** Per-categorie totals of one invoice, largest first (for the expand panel). */
function categorieTotalen(inv: Invoice): { categorie: string; bedrag: number }[] {
  const m = new Map<string, number>();
  for (const r of inv.regels) m.set(r.categorie, (m.get(r.categorie) ?? 0) + r.bedragExcl);
  return [...m.entries()]
    .map(([categorie, bedrag]) => ({ categorie, bedrag }))
    .sort((a, b) => b.bedrag - a.bedrag);
}

function maandBereik(inv: Invoice): string {
  const maanden = [...new Set(inv.regels.map((r) => r.maand).filter(Boolean))].sort();
  if (!maanden.length) return '—';
  const eerste = monthLabel(maanden[0]);
  const laatste = monthLabel(maanden[maanden.length - 1]);
  return eerste === laatste ? eerste : `${eerste} – ${laatste}`;
}

/** The plates on an invoice, without the placeholders the MTC import produces. */
function kentekens(inv: Invoice): string[] {
  const uniek = new Set(
    inv.regels.map((r) => r.kenteken).filter((k) => k && k !== 'FICTIEF' && k !== 'ONBEKEND'),
  );
  return [...uniek].sort();
}

/**
 * Which car(s) an invoice is about, short enough for the row.
 *
 * An MTC statement covers the whole fleet, so listing twenty plates there is
 * noise — the count is the useful summary. A maintenance invoice is about one car
 * and that plate is the single most identifying thing about it, which is exactly
 * what the list was missing.
 */
function kentekenLabel(inv: Invoice): string | null {
  const ks = kentekens(inv);
  if (!ks.length) return null;
  if (ks.length <= 3) return ks.join(', ');
  return `${ks.length} kentekens`;
}

/** Per-kenteken totals, largest first (for the expand panel). */
function kentekenTotalen(inv: Invoice): { kenteken: string; bedrag: number }[] {
  const m = new Map<string, number>();
  for (const r of inv.regels) m.set(r.kenteken, (m.get(r.kenteken) ?? 0) + r.bedragExcl);
  return [...m.entries()]
    .map(([kenteken, bedrag]) => ({ kenteken, bedrag }))
    .sort((a, b) => b.bedrag - a.bedrag);
}

/** Kop boven een blok in het uitklappaneel. */
const PANEEL_KOP: CSSProperties = {
  color: '#8C8C82',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  marginBottom: 8,
};

/** Bedragen onder elkaar lijnen alleen uit met cijfers van gelijke breedte. */
const BEDRAG: CSSProperties = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

/**
 * Het linkerblok van het uitklappaneel: wat deze factuur is, als een bonnetje.
 *
 * Een tankpasfactuur laat de categorieën zien met het totaal eronder; een
 * onderhoudsfactuur heeft maar één categorie, en daar wil je juist weten wélke
 * beurt het was, bij welke garage en hoeveel btw erover ging. De regel zelf
 * toont alleen het kenteken en het bedrag, dus dit is de plek voor de rest.
 */
function Samenvatting({ inv, alleen }: { inv: Invoice; alleen: boolean }) {
  const totaal = invoiceTotal(inv);
  // `links`: lopende tekst (omschrijving, garage) staat links en mag afbreken;
  // bedragen en de periode staan rechts, zodat ze onder elkaar uitlijnen.
  const rijen: { label: string; waarde: ReactNode; sterk?: boolean; links?: boolean }[] = [
    { label: 'Periode', waarde: maandBereik(inv) },
  ];
  if (inv.type === 'ONDERHOUD') {
    const omschrijving = inv.regels.map((r) => r.omschrijving).find(Boolean);
    const garage = inv.regels.map((r) => r.station).find(Boolean);
    const btw = inv.regels.reduce((s, r) => s + (r.bedragBtw ?? 0), 0);
    if (omschrijving) rijen.push({ label: 'Omschrijving', waarde: omschrijving, links: true });
    if (garage) rijen.push({ label: 'Garage', waarde: garage, links: true });
    rijen.push({ label: 'Excl. btw', waarde: euro(totaal), sterk: true });
    if (btw) rijen.push({ label: 'Btw', waarde: euro(btw) });
  } else {
    for (const c of categorieTotalen(inv)) {
      rijen.push({ label: c.categorie, waarde: euro(c.bedrag) });
    }
  }
  return (
    // Naast het autoblok een vaste, smalle kolom; alleen (onderhoud, één auto)
    // mag hij breder, want dan staat er een omschrijving en een garagenaam in.
    <div style={alleen ? { flex: '0 1 520px', minWidth: 0 } : { flex: '0 0 260px', minWidth: 0 }}>
      <div style={PANEEL_KOP}>Samenvatting</div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'max-content minmax(0, 1fr)',
          columnGap: 20,
          rowGap: 4,
          color: '#444444',
        }}
      >
        {rijen.map((r) => (
          <Fragment key={r.label}>
            <span style={{ color: '#8C8C82', whiteSpace: 'nowrap' }}>{r.label}</span>
            {/* Staat het blok alleen, dan is er geen kolom bedragen om op uit te
                lijnen en leest alles links als een gewone lijst. */}
            {r.links || alleen ? (
              <span style={{ overflowWrap: 'anywhere', fontWeight: r.sterk ? 700 : 400 }}>
                {r.waarde}
              </span>
            ) : (
              <span style={{ ...BEDRAG, textAlign: 'right', fontWeight: r.sterk ? 700 : 400 }}>
                {r.waarde}
              </span>
            )}
          </Fragment>
        ))}
        {inv.type !== 'ONDERHOUD' && (
          <>
            <span
              style={{
                gridColumn: '1 / -1',
                borderTop: '1px solid #E3D9CC',
                marginTop: 4,
              }}
            />
            <span style={{ color: '#111111', fontWeight: 700 }}>Totaal</span>
            <span style={{ ...BEDRAG, textAlign: 'right', fontWeight: 700, color: '#111111' }}>
              {euro(totaal)}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Het rechterblok: wat elke auto op deze factuur kostte, grootste eerst.
 *
 * Als CSS-kolommen (`columns`) en niet als één lange lijst of een wrap-regel:
 * de browser verdeelt 27 auto's dan zelf over drie kolommen van negen, van
 * boven naar beneden, en op een smal scherm worden dat er twee of één. Alles
 * blijft in beeld, zonder geneste scrollbalk die met die van de pagina vecht.
 * Elke cel is kenteken links en bedrag rechts, dus de bedragen lijnen uit.
 */
function PerAuto({ rijen }: { rijen: { kenteken: string; bedrag: number }[] }) {
  return (
    <div style={{ flex: '1 1 320px', minWidth: 0 }}>
      {/* Dezelfde telling als in de regel erboven: de plaatshouders FICTIEF en
          ONBEKEND staan wel in de lijst (dat geld is uitgegeven), maar zijn geen
          auto's en tellen dus niet mee. */}
      <div style={PANEEL_KOP}>
        Per auto (
        {rijen.filter((k) => k.kenteken !== 'FICTIEF' && k.kenteken !== 'ONBEKEND').length})
      </div>
      <div style={{ columns: '200px', columnGap: 32, color: '#444444' }}>
        {rijen.map((k) => (
          <div
            key={k.kenteken}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              padding: '2px 0',
              breakInside: 'avoid',
            }}
          >
            {/* De plaatshouders uit de MTC-import zijn geen auto's; daar valt
                niets aan te klikken. */}
            {k.kenteken === 'FICTIEF' || k.kenteken === 'ONBEKEND' ? (
              <span style={{ color: '#8C8C82' }}>{k.kenteken}</span>
            ) : (
              <AutoLink kenteken={k.kenteken} />
            )}
            <span style={{ ...BEDRAG, fontWeight: 700 }}>{euro(k.bedrag)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Title for the row. A hand-entered invoice has no filename, so falling back to
 * "—" left the seven ledger-imported maintenance invoices completely unidentifiable.
 * The line's own description is the next best thing.
 */
function titelVan(inv: Invoice): string {
  if (inv.bestandsnaam) return inv.bestandsnaam;
  const oms = inv.regels.map((r) => r.omschrijving).find(Boolean);
  if (oms) return oms;
  return inv.type === 'ONDERHOUD' ? 'Onderhoud (handmatig)' : 'Handmatige invoer';
}

/**
 * Everything one invoice can be found by, lowercased into a single string.
 *
 * Built once per invoice rather than per keystroke: an MTC statement holds 900+
 * cost lines, so reassembling this on every character typed would be noticeable.
 *
 * It searches the lines, not just the header, so a plate finds the MTC statement it
 * appears on as well as its own maintenance invoice — which is the useful answer to
 * "what did this car cost me". Months are `YYYY-MM`, so "2026-04" works as a filter
 * too.
 */
function zoekTekst(inv: Invoice): string {
  const delen = [
    inv.bestandsnaam ?? '',
    inv.factuurnummer ?? '',
    inv.datum ?? '',
    inv.type === 'ONDERHOUD' ? 'onderhoud' : 'mtc overzicht',
    titelVan(inv),
  ];
  const uniek = new Set<string>();
  for (const r of inv.regels) {
    uniek.add(r.kenteken);
    uniek.add(r.categorie);
    if (r.maand) uniek.add(r.maand);
    if (r.omschrijving) uniek.add(r.omschrijving);
    if (r.station) uniek.add(r.station);
  }
  return [...delen, ...uniek].join(' ').toLowerCase();
}

export function InvoiceList({
  invoices,
  onDelete,
  deletingId,
}: {
  invoices: Invoice[];
  onDelete: (id: string) => void;
  deletingId?: string;
}) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const [teVerwijderen, setTeVerwijderen] = useState<Invoice | null>(null);
  const [q, setQ] = useState('');

  // De zoektekst per factuur één keer opbouwen; alleen het filteren gebeurt per
  // toetsaanslag. Bij 2183 kostenregels is dat het verschil tussen merkbaar en niet.
  const metZoek = useMemo(() => invoices.map((inv) => ({ inv, hooi: zoekTekst(inv) })), [invoices]);
  const gefilterd = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return invoices;
    // Elk woord moet voorkomen, dus "p552bp banden" vindt alleen die ene factuur.
    const woorden = term.split(/\s+/);
    return metZoek
      .filter(({ hooi }) => woorden.every((w) => hooi.includes(w)))
      .map(({ inv }) => inv);
  }, [q, invoices, metZoek]);

  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <>
      {/* De paneelkop hieronder laat zijn eigen teller vallen: twee keer hetzelfde
          getal op één scherm. */}
      <ZoekBalk
        waarde={q}
        onWaarde={setQ}
        placeholder="Zoek op kenteken, garage, factuurnummer of maand…"
        ariaLabel="Facturen zoeken"
        aantal={
          gefilterd.length === invoices.length
            ? `${invoices.length} factu${invoices.length === 1 ? 'ur' : 'ren'}`
            : `${gefilterd.length} van ${invoices.length}`
        }
      />

      <div
        style={{
          background: '#FFFFFF',
          border: '1px solid #E3D9CC',
          borderRadius: 4,
          overflowX: 'auto',
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '2px solid #111111',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 12,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 700 }}>Verwerkte facturen</span>
        </div>

        {invoices.length === 0 && (
          <div style={{ padding: '28px 20px', fontSize: 14, color: '#888888' }}>
            Nog geen facturen verwerkt. Upload hierboven een tankpasfactuur, onderhoudsfactuur of
            CSV.
          </div>
        )}

        {invoices.length > 0 && gefilterd.length === 0 && (
          <div style={{ padding: '28px 20px', fontSize: 14, color: '#888888' }}>
            Geen factuur gevonden voor “{q.trim()}”.
          </div>
        )}

        {gefilterd.map((inv) => {
          const isOpen = open.has(inv.id);
          return (
            <div key={inv.id} style={{ borderBottom: '1px solid #F0E8DC' }}>
              <div
                onClick={() => toggle(inv.id)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  gap: 12,
                  padding: '12px 20px',
                  fontSize: 13,
                  alignItems: 'center',
                  cursor: 'pointer',
                  minWidth: 900,
                }}
              >
                <span style={{ color: '#8C8C82', fontSize: 11, textAlign: 'center' }}>
                  {isOpen ? '▾' : '▸'}
                </span>
                {/* "Tankpas" en niet "MTC-overzicht": onder type MTC vallen zowel de
                    MTC-uitdraai als de MoveMove-maandfactuur, en welke van de twee
                    het was staat al in de bestandsnaam ernaast. */}
                <Badge tone={inv.type === 'MTC' ? 'blue' : 'neutral'}>
                  {inv.type === 'MTC' ? 'Tankpas' : 'Onderhoud'}
                </Badge>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                  {inv.documentKey ? (
                    <a
                      href={invoiceDocumentUrl(inv.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      title="Origineel document openen"
                      style={{
                        ...ENTITEIT_LINK,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {inv.bestandsnaam ?? 'Document'}
                    </a>
                  ) : (
                    <span
                      style={{
                        color: '#111111',
                        fontWeight: 700,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {titelVan(inv)}
                    </span>
                  )}
                  {/* Kenteken vóór de referentie: dat is waar je op zoekt bij een
                    onderhoudsfactuur, en een factuurnummer zegt zonder auto niets. */}
                  <span
                    style={{
                      color: '#8C8C82',
                      fontSize: 12,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {[kentekenLabel(inv), inv.factuurnummer ? `Ref. ${inv.factuurnummer}` : null]
                      .filter(Boolean)
                      .join(' · ') || 'Geen referentie'}
                  </span>
                </span>
                <span>{fmtDate(inv.datum)}</span>
                <span style={{ color: '#666666' }}>
                  {inv.regels.length} {inv.regels.length === 1 ? 'regel' : 'regels'}
                </span>
                <span style={{ fontWeight: 700, textAlign: 'right' }}>
                  {euro(invoiceTotal(inv))}
                </span>
                <span style={{ textAlign: 'right' }}>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTeVerwijderen(inv);
                    }}
                    disabled={deletingId === inv.id}
                  >
                    {deletingId === inv.id ? 'Bezig…' : 'Verwijder'}
                  </Button>
                </span>
              </div>
              {isOpen && (
                <div
                  style={{
                    padding: '6px 20px 18px 56px',
                    display: 'flex',
                    gap: 40,
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    fontSize: 13,
                  }}
                >
                  <Samenvatting inv={inv} alleen={kentekenTotalen(inv).length <= 1} />
                  {/* Per auto uitgesplitst. Bij één auto staat het al in de regel
                    erboven, dus alleen tonen zodra er meer dan één is; daar is het
                    juist de vraag wat er per auto op de factuur staat. */}
                  {kentekenTotalen(inv).length > 1 && <PerAuto rijen={kentekenTotalen(inv)} />}
                </div>
              )}
            </div>
          );
        })}

        {teVerwijderen && (
          <ConfirmModal
            title="Import verwijderen?"
            // Het kenteken erbij: zonder bestandsnaam stond hier "Deze import wordt
            // verwijderd", en dan weet je bij zeven handmatige onderhoudsfacturen op
            // rij niet welke je aanklikt.
            message={`"${titelVan(teVerwijderen)}"${
              kentekenLabel(teVerwijderen) ? ` (${kentekenLabel(teVerwijderen)})` : ''
            } wordt verwijderd, inclusief alle ${teVerwijderen.regels.length} kostenregels. Dit is direct zichtbaar in het dashboard en op de passen.`}
            confirmLabel="Verwijderen"
            danger
            busy={deletingId === teVerwijderen.id}
            onConfirm={() => {
              onDelete(teVerwijderen.id);
              setTeVerwijderen(null);
            }}
            onClose={() => setTeVerwijderen(null)}
          />
        )}
      </div>
    </>
  );
}
